import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Mail, Phone, Globe, Building2, Sparkles, Send, ArrowRight, Newspaper, Users, MessageSquare, X, ChevronLeft, ChevronRight, CalendarDays, QrCode, Share2, Copy, Check, TrendingUp, Star, Trash2 } from "lucide-react";
import { ApplyDialog } from "@/components/CompanyBits";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import LocationMap from "@/components/LocationMap";

function StarRating({ value = 0, onChange, size = 4, readOnly = false }) {
  return (
    <div className="inline-flex items-center gap-0.5" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => !readOnly && onChange?.(n)}
          data-testid={`star-${n}`}
          className={`transition-transform ${readOnly ? "cursor-default" : "hover:scale-110 cursor-pointer"}`}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star className={`w-${size} h-${size} ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

function ReviewsSection({ companyId, initialAvg, initialCount }) {
  const { user, lang } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [avg, setAvg] = useState(initialAvg || 0);
  const [count, setCount] = useState(initialCount || 0);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    http.get(`/companies/${companyId}/reviews`).then((r) => {
      const items = r.data || [];
      setReviews(items);
      if (items.length > 0) {
        const s = items.reduce((a, x) => a + (x.rating || 0), 0);
        setAvg(Math.round((s / items.length) * 10) / 10);
        setCount(items.length);
      } else { setAvg(0); setCount(0); }
    }).catch(() => setReviews([]));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error(lang === "fr" ? "Connectez-vous pour laisser un avis" : "Sign in to leave a review"); return; }
    if (!rating) { toast.error(lang === "fr" ? "Choisissez une note (1-5)" : "Pick a rating (1-5)"); return; }
    setSaving(true);
    try {
      await http.post(`/companies/${companyId}/reviews`, { rating, comment: comment.trim() });
      toast.success(lang === "fr" ? "Avis publié !" : "Review posted!");
      setRating(0); setComment("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const remove = async (rid) => {
    if (!window.confirm(lang === "fr" ? "Supprimer cet avis ?" : "Delete this review?")) return;
    try {
      await http.delete(`/reviews/${rid}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const myReview = reviews.find((r) => r.user_id === user?.user_id);

  return (
    <div className="mt-10" data-testid="reviews-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500" />
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t(lang, "reviews_title")}</p>
          <span className="text-[10px] rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">{count}</span>
        </div>
        {avg > 0 && (
          <div className="flex items-center gap-2" data-testid="reviews-avg">
            <StarRating value={Math.round(avg)} readOnly size={4} />
            <span className="text-sm font-semibold text-slate-900">{avg.toFixed(1)}</span>
            <span className="text-xs text-slate-500">/ 5 · {count} {t(lang, "reviews_count_label")}</span>
          </div>
        )}
      </div>

      {/* Write / edit review */}
      {user ? (
        <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-5 mb-4">
          <p className="text-sm font-semibold text-[#1E3A8A] mb-2">{myReview ? t(lang, "reviews_edit_prompt") : t(lang, "reviews_write_prompt")}</p>
          <StarRating value={rating} onChange={setRating} size={6} />
          <textarea
            data-testid="review-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t(lang, "reviews_placeholder")}
            className="mt-3 w-full rounded-xl border border-slate-200 p-3 text-sm resize-none focus:outline-none focus:border-[#1E3A8A]"
            rows={3}
            maxLength={1000}
          />
          <div className="mt-3 flex items-center justify-end">
            <Button data-testid="review-submit-btn" type="submit" disabled={saving || !rating} className="bg-[#F97316] hover:bg-[#EA580C] rounded-full h-9 px-4 text-sm">
              {saving ? "…" : t(lang, "reviews_submit")}
            </Button>
          </div>
        </form>
      ) : (
        <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-200 p-5 text-center mb-4">
          <p className="text-sm text-slate-500">{t(lang, "reviews_login_prompt")}</p>
        </div>
      )}

      {/* Reviews list */}
      <div className="space-y-3">
        {reviews.map((r) => (
          <div key={r.id} data-testid={`review-${r.id}`} className="rounded-2xl bg-white border border-slate-200 p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-sm text-slate-900">{r.author_name}</p>
                  <StarRating value={r.rating} readOnly size={3.5} />
                  <span className="text-xs text-slate-400">
                    {new Intl.DateTimeFormat(lang === "fr" ? "fr-FR" : "en-US", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(r.created_at))}
                  </span>
                </div>
                {r.comment && <p className="text-sm text-slate-700 mt-2 leading-relaxed whitespace-pre-wrap">{r.comment}</p>}
              </div>
              {(user?.user_id === r.user_id || user?.user_type === "admin") && (
                <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r.id)} className="text-slate-400 hover:text-red-500 shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
        {reviews.length === 0 && (
          <p className="text-center text-sm text-slate-400 py-6">{t(lang, "reviews_empty")}</p>
        )}
      </div>
    </div>
  );
}

export default function Vitrine() {
  const { id } = useParams();
  const { user, lang } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [apply, setApply] = useState(false);
  const [lightbox, setLightbox] = useState(null); // gallery index or null
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimEmail, setClaimEmail] = useState("");
  const [claimRole, setClaimRole] = useState("");
  const [claimMsg, setClaimMsg] = useState("");
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimResult, setClaimResult] = useState(null); // { status, auto_approved }
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/vitrine/${id}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(lang === "fr" ? "Lien copié !" : "Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(lang === "fr" ? "Copie impossible" : "Copy failed");
    }
  };

  const downloadQr = () => {
    const svg = document.querySelector('[data-testid="vitrine-qr-svg"]');
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `proxijob_${(c?.name || "vitrine").replace(/\s+/g, "_")}_qr.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    http.get(`/companies/${id}`).then((r) => setC(r.data)).catch(() => setC(false));
    // http and setC are stable — the effect only cares about the route id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % (c?.gallery?.length || 1));
      else if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + (c?.gallery?.length || 1)) % (c?.gallery?.length || 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // setLightbox is stable; c is read via closure and refreshed by the parent
    // fetch effect above — no need to re-bind the listener on every gallery change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, c]);

  if (c === false) return <div className="p-10 text-center">Not found</div>;
  if (!c) return <div className="p-10 text-center">…</div>;

  const gallery = c.gallery || [];
  const team = c.team || [];
  const news = [...(c.news || [])].sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  function _extractDomain(url) {
    if (!url) return "";
    try {
      const u = new URL(url.startsWith("http") ? url : `http://${url}`);
      return u.hostname.replace(/^www\./, "");
    } catch { return ""; }
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#1E3A8A] flex items-center justify-center"><MapPin className="w-3.5 h-3.5 text-white" /></div>
            <span className="font-display font-bold text-[#1E3A8A]">Proxijob</span>
          </Link>
          <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{lang === "fr" ? "Fiche vitrine" : "Showcase"}</span>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 py-10">
        <div className="rounded-3xl bg-gradient-to-br from-[#1E3A8A] to-[#172554] text-white p-8 md:p-12 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-[#F97316]/20 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <div>
                <span className="text-xs uppercase tracking-[0.2em] text-white/70">{c.sector}</span>
                <h1 data-testid="vitrine-name" className="font-display text-4xl sm:text-5xl font-bold tracking-tight">{c.name}</h1>
              </div>
            </div>
            <p className="mt-4 text-white/90 max-w-2xl leading-relaxed text-lg">{c.description}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {c.sponsored && (
                <Badge data-testid="vitrine-sponsor-badge" className="bg-amber-300 text-amber-900 rounded-full">
                  <Star className="w-3 h-3 mr-1 fill-amber-900" /> {lang === "fr" ? "Sponsor" : "Sponsor"}
                </Badge>
              )}
              {c.hosts_interns && <Badge className="bg-[#F97316] text-white rounded-full">{t(lang, "hosts_interns_yes")}</Badge>}
              {(c.contract_types || []).map((ct) => <Badge key={ct} className="bg-white/20 text-white rounded-full backdrop-blur">{t(lang, `contract_${ct}`)}</Badge>)}
            </div>
            {(c.sponsored || c.boost_active) && c.application_count > 0 && (
              <div data-testid="vitrine-sponsor-success" className="mt-4 inline-flex items-center gap-2 text-xs text-white/75">
                <TrendingUp className="w-3.5 h-3.5 text-amber-300" />
                <span>
                  {c.application_count}{" "}
                  {lang === "fr" ? "candidatures reçues via Proxijob" : "applications received via Proxijob"}
                </span>
              </div>
            )}
            <Button data-testid="vitrine-apply-btn" onClick={() => setApply(true)} className="mt-6 bg-[#F97316] hover:bg-[#EA580C] text-white rounded-full h-11 px-6">
              <Send className="w-4 h-4 mr-2" /> {t(lang, "apply_spontaneous")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            {user && c.owner_user_id && c.owner_user_id !== user.user_id && (
              <Button
                data-testid="vitrine-message-btn"
                onClick={() => nav(`/messages?to=${c.owner_user_id}&company=${c.id}`)}
                variant="outline"
                className="mt-6 ml-2 bg-white/10 border-white/40 text-white hover:bg-white/20 rounded-full h-11 px-5"
              >
                <MessageSquare className="w-4 h-4 mr-2" /> {lang === "fr" ? "Message" : "Message"}
              </Button>
            )}
            {user && !c.owner_user_id && (
              <Button
                data-testid="vitrine-claim-btn"
                onClick={() => setClaimOpen(true)}
                variant="outline"
                className="mt-6 ml-2 bg-white/10 border-white/40 text-white hover:bg-white/20 rounded-full h-11 px-5"
              >
                <Building2 className="w-4 h-4 mr-2" /> {lang === "fr" ? "Je suis le propriétaire" : "I own this business"}
              </Button>
            )}
            <Button
              data-testid="vitrine-share-btn"
              onClick={() => setShareOpen(true)}
              variant="outline"
              className="mt-6 ml-2 bg-white/10 border-white/40 text-white hover:bg-white/20 rounded-full h-11 px-5"
            >
              <Share2 className="w-4 h-4 mr-2" /> {lang === "fr" ? "Partager" : "Share"}
            </Button>
          </div>
        </div>

        {/* Company own video (if present) */}
        {c.video_url && (
          <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[#F97316]" />
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{lang === "fr" ? "Vidéo entreprise" : "Company video"}</p>
            </div>
            <div className="aspect-video rounded-xl overflow-hidden bg-black">
              <iframe data-testid="vitrine-video" src={c.video_url} title={c.name} allow="autoplay; encrypted-media" allowFullScreen className="w-full h-full" />
            </div>
          </div>
        )}

        {/* Gallery */}
        {gallery.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-[#F97316]" />
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t(lang, "gallery")}</p>
              <span className="ml-auto text-[10px] text-slate-400">{lang === "fr" ? "Cliquez pour agrandir" : "Click to enlarge"}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {gallery.map((src, i) => (
                <button
                  key={`gallery-${src}-${i}`}
                  data-testid={`vitrine-gallery-${i}`}
                  onClick={() => setLightbox(i)}
                  className="group aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 hover:-translate-y-1 hover:shadow-lg transition-all cursor-zoom-in"
                >
                  <img src={src} alt={`gallery-${i}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Contact grid */}
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">{t(lang, "address")}</p>
            <p className="text-sm">{c.address}</p>
            <p className="text-sm text-slate-500">{c.city}, {c.country}</p>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">{t(lang, "contact")}</p>
            {c.email && <p className="text-sm flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {c.email}</p>}
            {c.phone && <p className="text-sm flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {c.phone}</p>}
            {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="text-sm text-[#1E3A8A] hover:underline flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> {c.website}</a>}
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">{t(lang, "size")}</p>
            <p className="text-sm">{c.size || "—"}</p>
          </div>
        </div>

        {/* Precise hiring location — a mini leaflet map with a single pin, so
            candidates see exactly where they'd work / interview / apply. */}
        {typeof c.lat === "number" && typeof c.lng === "number" && (
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-[#F97316]" />
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
                {lang === "fr" ? "Localisation exacte" : "Exact location"}
              </p>
            </div>
            <LocationMap
              lat={c.lat}
              lng={c.lng}
              name={c.name}
              address={c.address}
              city={c.city}
              country={c.country}
              lang={lang}
            />
          </div>
        )}

        {/* Team */}
        {team.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-[#1E3A8A]" />
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t(lang, "team")}</p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {team.map((m, i) => (
                <div key={`team-${m.name || "unknown"}-${i}`} data-testid={`vitrine-team-${i}`} className="rounded-2xl bg-white border border-slate-200 p-4 flex items-center gap-3">
                  <Avatar className="w-12 h-12">
                    {m.avatar ? <img src={m.avatar} alt={m.name} /> : <AvatarFallback className="bg-[#1E3A8A] text-white text-sm">{(m.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}</AvatarFallback>}
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{m.name}</p>
                    <p className="text-xs text-slate-500 truncate">{m.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* News timeline */}
        {news.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Newspaper className="w-4 h-4 text-[#F97316]" />
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t(lang, "news")}</p>
              <span className="ml-2 text-[10px] rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">{news.length}</span>
            </div>
            <div className="relative pl-6">
              <div className="absolute left-2 top-2 bottom-2 w-px bg-gradient-to-b from-[#F97316]/60 via-slate-200 to-transparent" />
              <div className="space-y-4">
                {news.map((n, i) => {
                  const dt = n.date ? new Date(n.date) : null;
                  const formatted = dt && !isNaN(dt.getTime())
                    ? new Intl.DateTimeFormat(lang === "fr" ? "fr-FR" : "en-US", { day: "2-digit", month: "short", year: "numeric" }).format(dt)
                    : (n.date || "");
                  return (
                    <div key={`news-${n.date || ""}-${n.title || i}`} data-testid={`vitrine-news-${i}`} className="relative">
                      <span className="absolute -left-[26px] top-3 w-3 h-3 rounded-full bg-[#F97316] ring-4 ring-[#FFEDD5]" />
                      <div className="rounded-2xl bg-white border border-slate-200 p-5 hover:shadow-md transition-shadow">
                        <div className="flex items-baseline justify-between gap-3 flex-wrap">
                          <p className="font-display font-semibold text-lg text-[#1E3A8A]">{n.title}</p>
                          {formatted && (
                            <span data-testid={`vitrine-news-date-${i}`} className="text-xs text-slate-500 whitespace-nowrap flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-full">
                              <CalendarDays className="w-3 h-3" /> {formatted}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mt-2 leading-relaxed whitespace-pre-wrap">{n.body}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {c.typical_needs?.length > 0 && (
          <div className="mt-10 rounded-2xl bg-white border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-[#F97316]" />
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t(lang, "typical_needs")}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {c.typical_needs.map((n) => <Badge key={n} variant="secondary" className="rounded-full text-sm">{n}</Badge>)}
            </div>
          </div>
        )}

        <ReviewsSection companyId={id} initialAvg={c.avg_rating} initialCount={c.review_count} />
      </section>

      <footer className="text-center py-8 text-xs text-slate-500 border-t border-slate-200">
        {t(lang, "footer_note")}
      </footer>

      <ApplyDialog open={apply} onOpenChange={setApply} company={c} />

      {/* Claim ownership modal */}
      {claimOpen && (
        <div data-testid="claim-modal" onClick={() => !claimBusy && setClaimOpen(false)} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-5 h-5 text-[#F97316]" />
              <h3 className="font-display text-xl font-bold text-[#1E3A8A]">
                {lang === "fr" ? "Revendiquer cette entreprise" : "Claim this business"}
              </h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              {lang === "fr"
                ? `Vous êtes propriétaire ou salarié de ${c.name} ? Envoyez une demande. Auto-approuvée si votre email correspond au domaine ${_extractDomain(c.website) || "de l'entreprise"}.`
                : `Are you the owner or an employee of ${c.name}? Auto-approved if your email matches the company's website domain.`}
            </p>

            {claimResult ? (
              <div data-testid="claim-result" className={`rounded-2xl p-4 text-sm ${claimResult.status === "approved" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
                {claimResult.status === "approved" ? (
                  <p>✅ {lang === "fr" ? `Approuvée automatiquement — vous êtes désormais propriétaire de « ${c.name} »` : `Auto-approved — you now own « ${c.name} »`}</p>
                ) : (
                  <p>⏳ {lang === "fr" ? "Demande envoyée aux administrateurs. Vous recevrez une notification une fois validée." : "Request sent to admins. You'll be notified once decided."}</p>
                )}
                <Button data-testid="claim-close-btn" onClick={() => { setClaimOpen(false); setClaimResult(null); }} className="mt-4 w-full bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-full">Ok</Button>
              </div>
            ) : (
              <div className="space-y-3">
                <input
                  data-testid="claim-email-input"
                  type="email"
                  placeholder={lang === "fr" ? "Email pro (ex : contact@entreprise.com)" : "Business email"}
                  value={claimEmail}
                  onChange={(e) => setClaimEmail(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-full border border-slate-200 text-sm focus:border-[#1E3A8A] outline-none"
                />
                <input
                  type="text"
                  placeholder={lang === "fr" ? "Votre rôle (CEO, RH, gérant…)" : "Your role (CEO, HR, manager…)"}
                  value={claimRole}
                  onChange={(e) => setClaimRole(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-full border border-slate-200 text-sm focus:border-[#1E3A8A] outline-none"
                />
                <textarea
                  placeholder={lang === "fr" ? "Message (optionnel)" : "Message (optional)"}
                  value={claimMsg}
                  onChange={(e) => setClaimMsg(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 rounded-2xl border border-slate-200 text-sm focus:border-[#1E3A8A] outline-none resize-none"
                />
                <div className="flex gap-2">
                  <Button data-testid="claim-cancel-btn" variant="outline" onClick={() => setClaimOpen(false)} className="flex-1 rounded-full">
                    {lang === "fr" ? "Annuler" : "Cancel"}
                  </Button>
                  <Button
                    data-testid="claim-submit-btn"
                    disabled={claimBusy || !claimEmail}
                    onClick={async () => {
                      setClaimBusy(true);
                      try {
                        const { data } = await http.post(`/companies/${c.id}/claim`, {
                          contact_email: claimEmail,
                          role_at_company: claimRole || undefined,
                          message: claimMsg || undefined,
                        });
                        setClaimResult({ status: data.status, auto_approved: data.auto_approved });
                        if (data.auto_approved) {
                          setC({ ...c, owner_user_id: user.user_id });
                        }
                      } catch (e) {
                        alert(e?.response?.data?.detail || (lang === "fr" ? "Erreur d'envoi" : "Send failed"));
                      } finally {
                        setClaimBusy(false);
                      }
                    }}
                    className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white rounded-full"
                  >
                    {claimBusy ? (lang === "fr" ? "Envoi…" : "Sending…") : (lang === "fr" ? "Envoyer" : "Send")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {lightbox !== null && gallery[lightbox] && (
        <div
          data-testid="vitrine-lightbox"
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="lightbox-close"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <span className="absolute top-5 left-5 text-white/80 text-xs tracking-widest uppercase">
            {lightbox + 1} / {gallery.length}
          </span>
          {gallery.length >= 2 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + gallery.length) % gallery.length); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                aria-label="Previous"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % gallery.length); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                aria-label="Next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <img
            src={gallery[lightbox]}
            alt={`gallery-${lightbox}`}
            className="max-w-[92vw] max-h-[88vh] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="vitrine-share-dialog" className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1E3A8A]">
              <QrCode className="w-5 h-5" /> {lang === "fr" ? "Partager cette vitrine" : "Share this page"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 pt-2">
            <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <QRCodeSVG
                data-testid="vitrine-qr-svg"
                value={shareUrl}
                size={220}
                level="M"
                fgColor="#1E3A8A"
                includeMargin
              />
            </div>
            <p className="text-xs text-slate-500 text-center max-w-xs">
              {lang === "fr"
                ? "Imprimez ce QR code sur un flyer, une vitrine ou une carte de visite : chaque scan ouvre votre fiche Proxijob."
                : "Print this QR code on a flyer, storefront or business card — every scan opens your Proxijob page."}
            </p>
            <div className="w-full flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 pl-4 pr-1 py-1">
              <span data-testid="vitrine-share-url" className="text-xs text-slate-700 truncate flex-1">{shareUrl}</span>
              <Button
                data-testid="vitrine-copy-link-btn"
                size="sm"
                onClick={copyLink}
                className="rounded-full h-8 px-3 bg-[#1E3A8A] hover:bg-[#172554] text-xs"
              >
                {copied ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                {copied ? (lang === "fr" ? "Copié" : "Copied") : (lang === "fr" ? "Copier" : "Copy")}
              </Button>
            </div>
            <Button
              data-testid="vitrine-download-qr-btn"
              variant="outline"
              onClick={downloadQr}
              className="rounded-full border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white"
            >
              <QrCode className="w-4 h-4 mr-2" /> {lang === "fr" ? "Télécharger le QR (SVG)" : "Download QR (SVG)"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
